import {
  get as networkGet,
  post as networkPost,
  patch as networkPatch,
  del as networkDel
} from '../../networkClient';
import { Path } from '../path';

export async function createLead(payload) {
  try {
    const response = await networkPost(
      Path.CREATE_LEAD,
      payload,
      null,
      null,
      true
    );
    return response?.data;
  } catch (error) {
    return {
      error: true,
      message: error.response?.data?.message || error.message,
      status: error.response?.status
    };
  }
}

export async function queryLeads({
  filter,
  sortBy = 'createdAt',
  sortOrder = 'desc',
  limit = 100,
  offset = 0
}) {
  try {
    const response = await networkPost(
      Path.QUERY_LEADS,
      { filter, sortBy, sortOrder, limit, offset },
      null,
      null,
      true
    );
    return response?.data;
  } catch (error) {
    return {
      error: true,
      message: error.message,
      status: error.response?.status
    };
  }
}

export async function createLeadWaitlist(leadId, { date, notes }) {
  try {
    const response = await networkPost(
      Path.LEAD_WAITLIST(leadId),
      { date, notes },
      null,
      null,
      true
    );
    return response?.data;
  } catch (error) {
    return {
      error: true,
      message: error.response?.data?.error || error.message,
      status: error.response?.status
    };
  }
}

export async function deleteLeadWaitlist(leadId, existingDate) {
  // networkDel does not throw on failure — it returns the axios error object.
  // Detect that shape and surface it as a structured error so the caller's
  // `result?.error` check actually fires.
  const response = await networkDel(
    Path.LEAD_WAITLIST(leadId),
    { existingDate },
    null,
    null,
    true,
    true
  );
  if (response?.isAxiosError || response?.response) {
    return {
      error: true,
      message:
        response?.response?.data?.error ||
        response?.message ||
        'Request failed',
      status: response?.response?.status
    };
  }
  return response?.data;
}

// Integrator's PG-backed lead response uses different field-name casing and
// shapes than the Mongo Lead schema the frontend was built against. Translate
// at the REST boundary so every consumer of `leadData` sees the shape it
// expects.
//
//   waitListNotes [{ waitlistDate, notes }]  →  waitlist [{ date, notes }]
//   followUpToggle (capital U)               →  followupToggle (lowercase u)
//   botTurningOffReason ["{\"a\",\"b\"}"]    →  ["a", "b"]
//     (integrator returns the PG text[] literal wrapped in a JS array;
//      iterate-and-display consumers need plain strings)
function normalizePgArrayLiteral(value) {
  if (!Array.isArray(value)) return value;
  if (
    value.length !== 1 ||
    typeof value[0] !== 'string' ||
    !value[0].startsWith('{') ||
    !value[0].endsWith('}')
  ) {
    return value;
  }
  const inner = value[0].slice(1, -1);
  if (!inner) return [];
  return inner
    .split(',')
    .map(s => s.trim().replace(/^"(.*)"$/, '$1'))
    .filter(Boolean);
}

function normalizeLeadShape(lead) {
  if (!lead) return lead;
  if (!lead.waitlist && Array.isArray(lead.waitListNotes)) {
    lead.waitlist = lead.waitListNotes.map(n => ({
      date: n.waitlistDate ?? n.date,
      notes: n.notes
    }));
  }
  if (lead.followupToggle === undefined && lead.followUpToggle !== undefined) {
    lead.followupToggle = lead.followUpToggle;
  }
  if (Array.isArray(lead.botTurningOffReason)) {
    lead.botTurningOffReason = normalizePgArrayLiteral(
      lead.botTurningOffReason
    );
  }
  return lead;
}

export async function getLead(leadId, { sensitive = false } = {}) {
  const response = await networkGet(
    Path.LEAD_BY_ID(leadId),
    sensitive ? { sensitive: true } : null,
    null,
    true,
    true
  );
  // networkGet returns the axios error object on failure (does not throw).
  // An axios error has `.response` attached; a successful response has `.data`.
  if (response?.isAxiosError || response?.response) {
    return {
      error: true,
      message:
        response?.response?.data?.error ||
        response?.message ||
        'Request failed',
      status: response?.response?.status
    };
  }
  return normalizeLeadShape(response?.data?.data);
}

export async function patchLead(leadId, record) {
  try {
    const response = await networkPatch(
      Path.LEAD_BY_ID(leadId),
      record,
      null,
      null,
      true,
      true
    );
    return response?.data;
  } catch (error) {
    return {
      error: true,
      message: error.response?.data?.error || error.message,
      status: error.response?.status
    };
  }
}

export async function transferLeadAgent(leadId, agent, reason) {
  try {
    const trimmedReason = reason?.trim();
    const response = await networkPost(
      Path.LEAD_AGENT(leadId),
      trimmedReason ? { agent, reason: trimmedReason } : { agent },
      null,
      null,
      true
    );
    return { data: response?.data?.data ?? null, error: null };
  } catch (error) {
    return {
      data: null,
      error: error.response?.data?.error || error.message
    };
  }
}
